import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';
import { adminApi } from '@/services/api';
import Button from '@/components/ui/Button';
import { getPrimaryImage } from '@/utils/imageUtils';
import {
  StarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface Review {
  id: number;
  product_id: number;
  product?: {
    id: number;
    name: string;
    images?: Array<{ image_url: string }>;
  };
  reviewer_id: number;
  reviewer?: {
    first_name: string;
    last_name: string;
  };
  rating: number;
  title: string;
  comment: string;
  is_approved: boolean;
  created_at: string;
}

const StaffReviewsPage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await adminApi.getPendingReviews();
      setReviews(response.data || []);
    } catch (error: any) {
      let errorMessage = 'Failed to fetch reviews';
      if (error?.detail) {
        if (typeof error.detail === 'string') {
          errorMessage = error.detail;
        } else if (Array.isArray(error.detail)) {
          errorMessage = error.detail.map((err: any) => err.msg || err.message || String(err)).join(', ');
        } else {
          errorMessage = String(error.detail);
        }
      }
      setError(errorMessage);
      console.error('Reviews fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (reviewId: number) => {
    try {
      await adminApi.approveReview(reviewId);
      fetchReviews(); // Refresh list
    } catch (error: any) {
      console.error('Failed to approve review:', error);
      alert('Failed to approve review');
    }
  };

  const handleReject = async (reviewId: number) => {
    const reason = prompt('Enter rejection reason:');
    if (reason) {
      try {
        await adminApi.rejectReview(reviewId, reason);
        fetchReviews(); // Refresh list
      } catch (error: any) {
        console.error('Failed to reject review:', error);
        alert('Failed to reject review');
      }
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon
        key={i}
        className={`h-5 w-5 ${
          i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  if (isLoading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reviews...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const errorMessage = typeof error === 'string' ? error : error?.toString() || 'An error occurred';
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading reviews</h3>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <Button onClick={fetchReviews}>Try Again</Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Pending Reviews</h1>
          <p className="mt-2 text-gray-600">Review and moderate pending review submissions</p>
        </div>

        {/* Reviews List */}
        {reviews.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No pending reviews</h3>
            <p className="text-gray-600">All reviews have been reviewed.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {reviews.map((review) => (
              <div key={review.id} className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <img
                      src={getPrimaryImage(review.product?.images)}
                      alt={review.product?.name || 'Product'}
                      className="w-20 h-20 object-cover rounded-md"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{review.product?.name || 'Unknown Product'}</h3>
                        <p className="text-sm text-gray-600">
                          by {review.reviewer?.first_name} {review.reviewer?.last_name}
                        </p>
                      </div>
                      <div className="flex items-center space-x-1">
                        {renderStars(review.rating)}
                      </div>
                    </div>
                    {review.title && (
                      <h4 className="text-md font-medium text-gray-900 mt-2">{review.title}</h4>
                    )}
                    <p className="text-gray-700 mt-2">{review.comment}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Submitted: {new Date(review.created_at).toLocaleDateString()}
                    </p>
                    <div className="flex space-x-2 mt-4">
                      <Button
                        size="sm"
                        onClick={() => handleApprove(review.id)}
                      >
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReject(review.id)}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <XCircleIcon className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffReviewsPage;

